import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowLeft, RefreshCw, Send, Users } from "lucide-react";
import {
  api,
  type ApiError,
  type BroadcastPreview,
  type BroadcastSegment,
  type CampaignDetail,
  type CampaignRow,
} from "../api";
import { fmtDateTime, fmtInt } from "../lib/format";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { EmptyState } from "../components/ui/empty-state";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { StatusChip } from "../components/ui/ledger";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const SEGMENTS: BroadcastSegment[] = ["all", "debtors", "lapsed"];
const TEMPLATE_KEYS = ["promo", "comeback", "thanks"] as const;
const MAX_LEN = 1000;

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation("broadcast");
  const tone =
    status === "completed"
      ? "ok"
      : status === "failed" || status === "stopped"
        ? "danger"
        : status === "running"
          ? "warn"
          : "muted";
  return <StatusChip tone={tone as never}>{t(`status.${status}`)}</StatusChip>;
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="broadcast-stat rounded-lg border bg-card px-3 py-2.5">
      <div
        className={
          "font-num text-2xl font-bold " +
          (tone === "ok"
            ? "text-emerald-600"
            : tone === "warn"
              ? "text-amber-600"
              : tone === "danger"
                ? "text-destructive"
                : "text-foreground")
        }
      >
        {fmtInt(value)}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

export function BroadcastPage() {
  const { t } = useTranslation("broadcast");

  const [segment, setSegment] = useState<BroadcastSegment>("all");
  const [lapsedMonths, setLapsedMonths] = useState(3);
  const [template, setTemplate] = useState("free");
  const [text, setText] = useState("");

  const [preview, setPreview] = useState<BroadcastPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [detail, setDetail] = useState<CampaignDetail | null>(null);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const p = await api.broadcastPreview({
        segment,
        lapsed_months: segment === "lapsed" ? lapsedMonths : null,
      });
      setPreview(p);
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [segment, lapsedMonths]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      setCampaigns(await api.listCampaigns());
    } catch {
      setCampaigns([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  function pickTemplate(key: string) {
    setTemplate(key);
    if (key === "free") {
      setText("");
      return;
    }
    // {{name}} literalini saqlaymiz — backend har bir mijoz uchun almashtiradi.
    setText(t(`compose.templates.${key}`, { name: "{{name}}" }));
  }

  async function submit() {
    const body = text.trim();
    if (!body) {
      toast.error(t("send.emptyText"));
      return;
    }
    setSending(true);
    try {
      await api.createCampaign({
        segment,
        text: body,
        campaign_uuid: crypto.randomUUID(),
        lapsed_months: segment === "lapsed" ? lapsedMonths : null,
        template_key: template === "free" ? null : template,
      });
      toast.success(t("send.queued"));
      setConfirmOpen(false);
      setText("");
      setTemplate("free");
      await Promise.all([loadList(), loadPreview()]);
    } catch (e) {
      toast.error((e as ApiError)?.message || t("send.error"));
    } finally {
      setSending(false);
    }
  }

  async function openDetail(id: number) {
    try {
      setDetail(await api.campaignDetail(id));
    } catch (e) {
      toast.error((e as ApiError)?.message || t("send.error"));
    }
  }

  const willReceive = preview?.will_receive ?? 0;
  const canSend = !!text.trim() && willReceive > 0 && !previewLoading;

  if (detail) {
    return <CampaignDetailView detail={detail} onBack={() => setDetail(null)} />;
  }

  return (
    <div className="broadcast-page space-y-5">
      <div className="grid gap-5 lg:grid-cols-2">
        {/* Segment + composer */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("compose.textLabel")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="bc-segment">{t("segment.label")}</Label>
              <select
                id="bc-segment"
                value={segment}
                onChange={(e) => setSegment(e.target.value as BroadcastSegment)}
                className="ui-control w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                {SEGMENTS.map((s) => (
                  <option key={s} value={s}>
                    {t(`segment.${s}`)}
                  </option>
                ))}
              </select>
            </div>

            {segment === "lapsed" && (
              <div className="space-y-1.5">
                <Label htmlFor="bc-months">{t("segment.lapsedMonths")}</Label>
                <div className="flex items-center gap-2">
                  <input
                    id="bc-months"
                    type="number"
                    min={1}
                    max={60}
                    value={lapsedMonths}
                    onChange={(e) =>
                      setLapsedMonths(
                        Math.min(60, Math.max(1, Number(e.target.value) || 1)),
                      )
                    }
                    className="ui-control w-24 h-9 rounded-md border border-input bg-background px-3 text-sm"
                  />
                  <span className="text-sm text-muted-foreground">
                    {t("segment.months")}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="bc-template">{t("compose.templateLabel")}</Label>
              <select
                id="bc-template"
                value={template}
                onChange={(e) => pickTemplate(e.target.value)}
                className="ui-control w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="free">{t("compose.free")}</option>
                {TEMPLATE_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {t(`compose.templates.${k}`, { name: "…" })}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="bc-text">{t("compose.textLabel")}</Label>
              <textarea
                id="bc-text"
                value={text}
                maxLength={MAX_LEN}
                rows={5}
                onChange={(e) => setText(e.target.value)}
                className="ui-control w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {t("compose.nameHint")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t("compose.charCount", { count: text.length })}
                </span>
              </div>
            </div>

            <Button
              className="w-full"
              disabled={!canSend}
              onClick={() => setConfirmOpen(true)}
            >
              <Send />
              {t("send.button")}
            </Button>
          </CardContent>
        </Card>

        {/* Reachability preview */}
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">{t("preview.title")}</CardTitle>
            <Button variant="ghost" size="sm" onClick={loadPreview} disabled={previewLoading}>
              <RefreshCw className={previewLoading ? "animate-spin" : ""} />
              {t("preview.refresh")}
            </Button>
          </CardHeader>
          <CardContent>
            {previewLoading && !preview ? (
              <Skeleton className="h-40 w-full" />
            ) : !preview || preview.targeted === 0 ? (
              <EmptyState icon={<Users />} title={t("preview.empty")} />
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2.5">
                  <Stat label={t("preview.targeted")} value={preview.targeted} />
                  <Stat label={t("preview.willReceive")} value={preview.will_receive} tone="ok" />
                  <Stat label={t("preview.optedOut")} value={preview.opted_out} tone="warn" />
                  <Stat label={t("preview.capped")} value={preview.capped} tone="warn" />
                </div>
                {preview.unlinked > 0 && (
                  <div className="broadcast-warning rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                    {t("preview.unlinkedHint", { count: preview.unlinked })}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Campaign history */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("history.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {listLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : campaigns.length === 0 ? (
            <EmptyState icon={<Send />} title={t("history.empty")} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("history.segment")}</TableHead>
                  <TableHead>{t("history.status")}</TableHead>
                  <TableHead className="text-right">{t("history.sent")}</TableHead>
                  <TableHead>{t("history.created")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{t(`segment.${c.segment}`)}</TableCell>
                    <TableCell>
                      <StatusBadge status={c.status} />
                    </TableCell>
                    <TableCell className="text-right font-num">
                      {fmtInt(c.sent)}/{fmtInt(c.targeted)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {fmtDateTime(c.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => openDetail(c.id)}>
                        {t("history.view")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogHeader>
          <DialogTitle>{t("send.confirmTitle")}</DialogTitle>
          <DialogDescription>
            {willReceive > 0
              ? t("send.confirmBody", { count: willReceive })
              : t("send.noRecipients")}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm whitespace-pre-wrap">
            {text}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>
            {t("send.cancel")}
          </Button>
          <Button onClick={submit} disabled={sending || willReceive === 0}>
            <Send />
            {sending ? t("send.sending") : t("send.confirm")}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

function CampaignDetailView({
  detail,
  onBack,
}: {
  detail: CampaignDetail;
  onBack: () => void;
}) {
  const { t } = useTranslation("broadcast");
  return (
    <div className="space-y-5">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft />
        {t("back")}
      </Button>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">
            {t(`segment.${detail.segment}`)} · {fmtDateTime(detail.created_at)}
          </CardTitle>
          <StatusBadge status={detail.status} />
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">
              {t("funnel.message")}
            </div>
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm whitespace-pre-wrap">
              {detail.text}
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold mb-2">{t("funnel.title")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <Stat label={t("funnel.targeted")} value={detail.targeted} />
              <Stat label={t("funnel.reachable")} value={detail.reachable} />
              <Stat label={t("funnel.sent")} value={detail.sent} tone="ok" />
              <Stat label={t("funnel.failed")} value={detail.failed} tone="danger" />
              <Stat label={t("funnel.skippedOptedOut")} value={detail.skipped_opted_out} tone="warn" />
              <Stat label={t("funnel.skippedCapped")} value={detail.skipped_capped} tone="warn" />
              <Stat label={t("funnel.skippedUnlinked")} value={detail.skipped_unlinked} tone="warn" />
              <Stat label={t("funnel.skippedBudget")} value={detail.skipped_budget} tone="warn" />
            </div>
          </div>

          {detail.skipped_unlinked > 0 && (
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
              {t("funnel.unlinkedHint")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
